
import { isInAppBrowser } from './webviewDetect';

/** Returns an AbortSignal that fires after `ms` — longer for in-app browsers. */
export function getTimeoutSignal(normalMs = 20_000): { signal: AbortSignal; clear: () => void } {
  const ms = isInAppBrowser ? 30_000 : normalMs;
  const controller = new AbortController();
  const id = setTimeout(() => controller.abort(), ms);
  return { signal: controller.signal, clear: () => clearTimeout(id) };
}

const TRANSIENT_MESSAGES = [
  'failed to fetch',
  'load failed',
  'network request failed',
  'networkerror',
  'aborted',
  'timed out',
  'timeout',
];

function isTransientNetworkError(err: unknown): boolean {
  if (err instanceof DOMException && err.name === 'AbortError') return true;
  if (err instanceof TypeError) return true; // all TypeErrors from fetch are network-level
  if (err instanceof Error) {
    const msg = err.message.toLowerCase();
    return TRANSIENT_MESSAGES.some(p => msg.includes(p));
  }
  return false;
}

/**
 * Wraps an async function with automatic retries on transient network errors.
 * Uses exponential back-off with jitter. Safe for production — only retries
 * network-level failures, not HTTP/application errors.
 */
export async function retryOnTransientNetwork<T>(
  fn: () => Promise<T>,
  { retries = 2, baseDelayMs = 1000 } = {},
): Promise<T> {
  let lastError: unknown;
  const maxRetries = isInAppBrowser ? Math.max(retries, 2) : retries;

  for (let attempt = 0; attempt <= maxRetries; attempt++) {
    try {
      return await fn();
    } catch (err) {
      lastError = err;
      if (attempt < maxRetries && isTransientNetworkError(err)) {
        const delay = baseDelayMs * 2 ** attempt + Math.random() * 500;
        await new Promise(r => setTimeout(r, delay));
        continue;
      }
      throw err;
    }
  }
  throw lastError; // unreachable but satisfies TS
}
